import React, { useCallback, useEffect, useRef, useState } from 'react'
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { CompassIcon, LegendsIcon, VenuImage, ZoomInImage, ZoomOutImage } from '../../Constant/AppConstant';
import style from "./index.module.css";
import { GrFormClose } from "react-icons/gr";


const MAX_SCALE = 15;
const NARROW_MQ = "(max-width: 600px)";
const MOBILE_INITIAL_SCALE = 1.5;

const Legends = [
    { label: "Diamond, Gemstone & Other Studded Jewellery", color: "#006bd6", backgroundColor: "#5488a5" },
    { label: "Gold & Gold CZ Studded Jewellery", color: "#ff7d05", backgroundColor: "#bb8a44" },
    { label: "Silver Jewellery, Artifacts & Gifting Items", color: "#727271", backgroundColor: "#3f4040" },
    { label: "Laboratories & Education", color: "#efd01d", backgroundColor: "#6f6d99" },
    { label: "Lab Grown Diamonds (Loose)", color: "#989898", backgroundColor: "#afaeaf" },
    { label: "Lab Grown Diamonds (Jewellery)", color: "#00b800", backgroundColor: "#48776a" },
    { label: "International Jewellery", color: "#fed7e0", backgroundColor: "#b3776d" },
    { label: "Loose stone (Colour stone)", color: "#ff00ff", backgroundColor: "#af2723" },
    { label: "The Select Club", color: "#930686", backgroundColor: "#ff0afe" },
    { label: "Loose Stones (Diamonds)", color: "#8d0091", backgroundColor: "#643358" },
    { label: "Allied", color: "#d10000" , backgroundColor: "#23454a" },
    { label: "Machinery", color: "#d10000", backgroundColor: "#556468" },
]

function getIsNarrow() {
    return typeof window !== "undefined" && window.matchMedia(NARROW_MQ).matches;
}

function getMobileStartTransform() {
    return {
        scale: MOBILE_INITIAL_SCALE,
        positionX: window.innerWidth * -0.4, // -40vw
        positionY: window.innerHeight * 0.05, // ~5vh
    };
}

function useIsNarrow() {
    const [isNarrow, setIsNarrow] = useState(getIsNarrow);

    useEffect(() => {
        const mq = window.matchMedia(NARROW_MQ);
        const onChange = () => setIsNarrow(mq.matches);
        onChange();
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    return isNarrow;
}

export default function CustomTransformWrapper(props) {
    const wrapperContext = useRef(null)
    const [legendsOpen, setLegendsOpen] = useState(false);
    const [showLayout, setShowLayout] = useState(false);
    const isNarrow = useIsNarrow();

    const applyMobileStart = useCallback((ref) => {
        if (!getIsNarrow()) {
            return false;
        }
        const instance = ref?.instance || ref;
        const wrapper = instance?.wrapperComponent || ref?.instance?.wrapperComponent;
        const setTransform = ref?.setTransform;
        if (!wrapper || !setTransform) {
            return false;
        }
        const svg = wrapper.querySelector("svg");
        if (!svg || !svg.offsetWidth || !svg.offsetHeight) {
            return false;
        }
        if (wrapper.offsetWidth < 50 || wrapper.offsetHeight < 50) {
            return false;
        }

        const { scale, positionX, positionY } = getMobileStartTransform();
        setTransform(positionX, positionY, scale, 0);
        console.log("[floor-plan fit]", {
            scale,
            positionX: Math.round(positionX),
            positionY: Math.round(positionY),
            wrapper: `${wrapper.offsetWidth}x${wrapper.offsetHeight}`,
            svg: `${svg.offsetWidth}x${svg.offsetHeight}`,
        });
        return true;
    }, []);

    // Re-apply after layout settles (splash / SVG size / DevTools device frame)
    useEffect(() => {
        if (!isNarrow) {
            return undefined;
        }
        const timers = [50, 200, 500, 1000].map((ms) =>
            setTimeout(() => {
                if (wrapperContext.current) {
                    applyMobileStart(wrapperContext.current);
                }
            }, ms)
        );
        return () => timers.forEach(clearTimeout);
    }, [isNarrow, applyMobileStart]);

    const mobileX = typeof window !== "undefined" ? window.innerWidth * -0.4 : 0;
    const mobileY = typeof window !== "undefined" ? window.innerHeight * 0.05 : 0;

    return (
        <>
            <TransformWrapper
                key={isNarrow ? "mobile" : "desktop"}
                initialScale={isNarrow ? MOBILE_INITIAL_SCALE : 1}
                initialPositionX={isNarrow ? mobileX : 0}
                initialPositionY={isNarrow ? mobileY : 0}
                // Free pan on narrow screens; no snap-back to left edge
                limitToBounds={!isNarrow}
                alignmentAnimation={{
                    disabled: true,
                    sizeX: 0,
                    sizeY: 0,
                }}
                velocityAnimation={{
                    disabled: isNarrow,
                }}
                panning={{
                    velocityDisabled: isNarrow,
                }}
                zoomAnimation={{
                    disabled: isNarrow,
                    animationTime: 200,
                }}
                pinch={isNarrow ? {
                    step: 2
                } : {}}
                maxScale={MAX_SCALE}
                onInit={(ref) => {
                    wrapperContext.current = ref;
                    const tryFit = () => applyMobileStart(ref);
                    tryFit();
                    requestAnimationFrame(tryFit);
                    setTimeout(tryFit, 150);
                    setTimeout(tryFit, 500);
                }}
                onPanningStop={(ref) => {
                    console.log("[floor-plan pan]", {
                        scale: Number(ref.state.scale.toFixed(3)),
                        positionX: Math.round(ref.state.positionX),
                        positionY: Math.round(ref.state.positionY),
                    });
                }}
                onZoomStop={(ref) => {
                    console.log("[floor-plan zoom]", {
                        scale: Number(ref.state.scale.toFixed(3)),
                        positionX: Math.round(ref.state.positionX),
                        positionY: Math.round(ref.state.positionY),
                    });
                }}
                ref={wrapperContext}
            >
                {({ state, zoomIn, zoomOut, resetTransform, zoomToElement, ...rest }) => (
                    <React.Fragment>
                        <div className={style.toolkit}>

                            <button onClick={() => zoomIn(0.25)}>
                                <img src={ZoomInImage} alt="zoomIn" />
                            </button>
                            <span title={`raw scale ${state.scale.toFixed(3)}`}>
                                {state.scale.toFixed(2)}x
                            </span>
                            <button onClick={() => zoomOut()}>
                                <img src={ZoomOutImage} alt="zoomOut" />
                            </button>
                        </div>
                        <div className={`${style.legendsContainer} `}>
                            <div className={`${style.legendsBox} ${legendsOpen ? style.legend_open : ""}`}>
                                <div className={`${style.dflex} ${style.justifyBetween}`}>
                                    <h3>
                                        LEGEND
                                    </h3>
                                    <img src={CompassIcon} alt={"compassIcon"} />
                                </div>
                                <div className={`${style.legendschildContianer} `} >
                                    {
                                        Legends.map(l => (
                                            <div key={l.label} className={style.legendSingle}>
                                                <span>
                                                    {
                                                        l.label
                                                    }
                                                </span>
                                                <span className={style.legendsBox_set} style={{
                                                    borderColor: l.color,
                                                    backgroundColor: l.backgroundColor
                                                }}>

                                                </span>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                            <div className={style.legendsIcon} onClick={() => {
                                setLegendsOpen(prev => !prev)
                            }}>
                                <img src={LegendsIcon} alt={"legends"} />
                            </div>

                        </div>
                        <TransformComponent
                            wrapperStyle={isNarrow ? { width: "100%", height: "100%" } : undefined}
                            contentStyle={{ display: "block" }}
                        >
                            {
                                props.children({
                                    zoomToElement,
                                    resetTransform
                                })
                            }
                        </TransformComponent>
                    </React.Fragment>
                )
                }
            </TransformWrapper >
            {
                <div className={`${style.modalContainer} ${showLayout ? style.modalContainer_visible : ""}`}>
                    <div className={style.modalParent}>
                        <div className={style.closeBtn} onClick={() => {
                            setShowLayout(false);
                        }} >
                            <GrFormClose />
                        </div>
                        <img src={VenuImage} alt={"layout"} />
                    </div>
                </div>
            }
        </>
    )
}
